import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import axios from 'axios'
import { api } from '@/services/api'
import router from '@/router'
import type {
  User,
  LoginRequest,
  RegisterRequest,
  OrganizationSummary,
  LoginResponse,
  TwoFactorLoginRequest,
  RecoveryCodeLoginRequest
} from '@/types'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const isInitialized = ref(false)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // Two-Factor Authentication state
  const pendingTwoFactor = ref(false)
  const requiresTwoFactorSetup = ref(false)

  // Getters
  const isAuthenticated = computed(() => !!user.value)
  const isAdmin = computed(() => user.value?.roles.includes('Admin') ?? false)
  const userEmail = computed(() => user.value?.email ?? '')

  // Organization getters
  const currentOrganization = computed<OrganizationSummary | null>(() => user.value?.organization ?? null)
  const organizations = computed<OrganizationSummary[]>(() => user.value?.organizations ?? [])
  const hasOrganization = computed(() => !!currentOrganization.value)
  const isOrgOwner = computed(() => currentOrganization.value?.role === 'Owner')
  const isOrgAdmin = computed(() => {
    const role = currentOrganization.value?.role
    return role === 'Owner' || role === 'Admin'
  })

  // Helper to determine post-login destination
  function getPostLoginDestination(): string {
    // Use server-side onboarding status
    if (!user.value?.onboardingCompleted) {
      return '/onboarding'
    }

    return isAdmin.value ? '/admin/contacts' : '/dashboard'
  }

  // Actions
  async function login(credentials: LoginRequest): Promise<void> {
    isLoading.value = true
    error.value = null
    pendingTwoFactor.value = false
    requiresTwoFactorSetup.value = false

    try {
      const response = await api.post<LoginResponse>('/auth/login', credentials)

      // Check if 2FA is required
      if (response.data.requiresTwoFactor) {
        pendingTwoFactor.value = true
        // Store intended redirect for after 2FA
        const redirect = router.currentRoute.value.query.redirect as string
        await router.push({ path: '/login/2fa', query: redirect ? { redirect } : undefined })
        return
      }

      // Check if 2FA setup is required (org enforcement)
      if (response.data.requiresTwoFactorSetup) {
        requiresTwoFactorSetup.value = true
        await router.push('/account/2fa-required')
        return
      }

      // Standard login success
      user.value = response.data.user!

      // Redirect to intended destination or smart default
      const redirect = router.currentRoute.value.query.redirect as string
      await router.push(redirect || getPostLoginDestination())
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : 'Login failed. Please check your credentials.'
      error.value = errorMessage
      throw new Error(errorMessage)
    } finally {
      isLoading.value = false
    }
  }

  async function verifyTwoFactor(request: TwoFactorLoginRequest, redirectUrl?: string): Promise<void> {
    isLoading.value = true
    error.value = null

    try {
      const response = await api.post<LoginResponse>('/auth/login-2fa', request)

      user.value = response.data.user!
      pendingTwoFactor.value = false

      // Redirect to intended destination (OAuth redirect takes priority, then query param, then default)
      const redirect = redirectUrl || (router.currentRoute.value.query.redirect as string)
      await router.push(redirect || getPostLoginDestination())
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : 'Invalid verification code.'
      error.value = errorMessage
      throw new Error(errorMessage)
    } finally {
      isLoading.value = false
    }
  }

  async function verifyRecoveryCode(request: RecoveryCodeLoginRequest, redirectUrl?: string): Promise<void> {
    isLoading.value = true
    error.value = null

    try {
      const response = await api.post<LoginResponse>('/auth/login-recovery', request)

      user.value = response.data.user!
      pendingTwoFactor.value = false

      // Redirect to intended destination (OAuth redirect takes priority, then query param, then default)
      const redirect = redirectUrl || (router.currentRoute.value.query.redirect as string)
      await router.push(redirect || getPostLoginDestination())
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : 'Invalid recovery code.'
      error.value = errorMessage
      throw new Error(errorMessage)
    } finally {
      isLoading.value = false
    }
  }

  function cancelTwoFactor(): void {
    pendingTwoFactor.value = false
    requiresTwoFactorSetup.value = false
    router.push('/login')
  }

  async function complete2faSetup(): Promise<void> {
    isLoading.value = true
    error.value = null

    try {
      const response = await api.post<LoginResponse>('/auth/complete-2fa-setup')

      user.value = response.data.user!
      requiresTwoFactorSetup.value = false

      // Redirect to dashboard after successful 2FA setup
      await router.push(getPostLoginDestination())
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to complete 2FA setup.'
      error.value = errorMessage
      throw new Error(errorMessage)
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Registers + auto-logs-in the user. Returns an optional non-fatal notice from the server
   * (e.g. the account was created but the invited org has hit its seat limit) so the caller can
   * surface it; null when there's nothing to show.
   */
  async function register(data: RegisterRequest): Promise<string | null> {
    isLoading.value = true
    error.value = null

    try {
      const response = await api.post<{ user: User; notice?: string | null }>('/auth/register', data)
      // Auto-login: set user from response
      user.value = response.data.user
      // New users always go to onboarding
      await router.push('/onboarding')
      return response.data.notice ?? null
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Registration failed.'
      error.value = errorMessage
      throw new Error(errorMessage)
    } finally {
      isLoading.value = false
    }
  }

  async function logout(): Promise<void> {
    try {
      await api.post('/auth/logout')
    } catch {
      // Ignore logout errors - we'll clear local state anyway
    } finally {
      user.value = null
      await router.push({ name: 'login' })
    }
  }

  async function checkAuth(): Promise<void> {
    if (isInitialized.value) return

    isLoading.value = true
    try {
      // Add timeout to prevent hanging indefinitely
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 10000) // 10 second timeout

      const response = await api.get<User>('/auth/me', {
        signal: controller.signal
      })
      clearTimeout(timeoutId)
      user.value = response.data
      error.value = null
    } catch (err) {
      user.value = null

      // If access token expired (401), try refreshing it silently
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        try {
          await axios.post('/api/auth/refresh', {}, { withCredentials: true })
          const retryResponse = await api.get<User>('/auth/me')
          user.value = retryResponse.data
          error.value = null
        } catch {
          // Refresh token also invalid - user must log in again
          user.value = null
        }
      } else if (err instanceof Error) {
        // Check if it's a server error (500) or network error
        if (err.name === 'AbortError') {
          error.value = 'Connection timed out. Please check if the server is running.'
        } else if (err.message.includes('500') || err.message.includes('Network')) {
          error.value = 'Unable to connect to server. Please try again later.'
        }
      }
    } finally {
      isInitialized.value = true
      isLoading.value = false
    }
  }

  async function refreshUser(): Promise<void> {
    try {
      const response = await api.get<User>('/auth/me')
      user.value = response.data
    } catch {
      // Silently fail - user state remains as is
    }
  }

  async function completeOnboarding(): Promise<void> {
    try {
      await api.post('/auth/complete-onboarding')
      // Update local user state
      if (user.value) {
        user.value = { ...user.value, onboardingCompleted: true }
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to complete onboarding.'
      throw new Error(errorMessage)
    }
  }

  async function forgotPassword(email: string): Promise<void> {
    isLoading.value = true
    error.value = null

    try {
      await api.post('/auth/forgot-password', { email })
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send reset email.'
      error.value = errorMessage
      throw new Error(errorMessage)
    } finally {
      isLoading.value = false
    }
  }

  async function resetPassword(token: string, email: string, password: string): Promise<void> {
    isLoading.value = true
    error.value = null

    try {
      await api.post('/auth/reset-password', { token, email, password })
      await router.push({ name: 'login', query: { reset: 'true' } })
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to reset password.'
      error.value = errorMessage
      throw new Error(errorMessage)
    } finally {
      isLoading.value = false
    }
  }

  function clearError(): void {
    error.value = null
  }

  async function switchOrganization(organizationId: string): Promise<void> {
    isLoading.value = true
    error.value = null

    try {
      const response = await api.post<{ user: User }>(`/auth/switch-organization/${organizationId}`)
      user.value = response.data.user
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to switch organization.'
      error.value = errorMessage
      throw new Error(errorMessage)
    } finally {
      isLoading.value = false
    }
  }

  return {
    // State
    user,
    isInitialized,
    isLoading,
    error,
    pendingTwoFactor,
    requiresTwoFactorSetup,

    // Getters
    isAuthenticated,
    isAdmin,
    userEmail,
    currentOrganization,
    organizations,
    hasOrganization,
    isOrgOwner,
    isOrgAdmin,

    // Actions
    login,
    register,
    logout,
    checkAuth,
    refreshUser,
    completeOnboarding,
    forgotPassword,
    resetPassword,
    clearError,
    switchOrganization,
    verifyTwoFactor,
    verifyRecoveryCode,
    cancelTwoFactor,
    complete2faSetup
  }
})
